import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '../ui'
import { fetchMe } from '../lib/api'
import type { Me } from '../lib/api'
import './magic-link.css'

type Phase = 'checking' | 'signedIn' | 'noSession' | 'unavailable'

/**
 * Where Microsoft's callback sends the browser after a successful sign-in.
 *
 * The session already exists — the API set the refresh cookie before redirecting here — so this
 * page's only job is to ask the server who that is and go to the right place. It deliberately
 * asks rather than assuming: arriving at this URL is not proof of anything, since anyone can
 * type it, and a page that showed "you're signed in" because of its own path would be lying to
 * whoever did.
 */
export function EntraComplete() {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('checking')
  const [me, setMe] = useState<Me | null>(null)

  useEffect(() => {
    let cancelled = false

    fetchMe().then((result) => {
      if (cancelled) return

      // Could not ASK is not "no session". A network blip or a server error here used to be read
      // as a failed sign-in and bounced to the sign-in page with an error -- after a sign-in that
      // had in fact worked, and whose session cookie was sitting in the browser.
      if (!result.ok) {
        setPhase('unavailable')
        return
      }

      if (!result.data) {
        setPhase('noSession')
        return
      }

      setMe(result.data)
      setPhase('signedIn')
    })

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (phase !== 'noSession') return

    // No session at this URL means the redirect was not ours to trust. Back to sign-in, with the
    // same message every other failure gets.
    const timer = setTimeout(() => navigate('/auth/sign-in?error=entra_failed'), 1200)
    return () => clearTimeout(timer)
  }, [phase, navigate])

  return (
    <main className="magic-link rise-in">
      <div className="label-mono">Defenssive AI Security Manager</div>

      {phase === 'checking' ? (
        <>
          <h2>Finishing sign-in…</h2>
          <p className="text-muted">Confirming your session with Defenssive.</p>
        </>
      ) : phase === 'noSession' ? (
        <>
          <h2>That sign-in did not complete</h2>
          <p className="text-muted">Taking you back to sign in.</p>
        </>
      ) : phase === 'unavailable' ? (
        <>
          <h2>We could not confirm your sign-in just now</h2>
          <p className="text-muted" role="alert">
            Defenssive did not answer. Your sign-in may well have worked — try again in a moment.
          </p>
          <Button onClick={() => window.location.reload()}>Try again</Button>
        </>
      ) : (
        <>
          <h2>You&rsquo;re signed in</h2>
          <p className="text-muted">
            Signed in as {me?.email}. Nothing in your Microsoft tenant has changed.
          </p>
          {/*
            This said connecting a tenant was "not built", and it was right: there was no
            /connect route, and nothing in a production build called startConsent(). Both exist
            now, so this sends people there instead of telling them the product stops here.

            Still deliberately silent about whether a tenant is CONNECTED. The session says
            whether a workspace exists and nothing more; /connect asks the questions it can
            actually answer.
          */}
          {/* A session with no workspace is not only a new customer: it is also somebody in several
              workspaces (sign-in does not pick for them) and somebody with an invitation waiting.
              /connect shows whichever applies -- the chooser, the invitations, or connecting a
              tenant -- so this names the step without guessing which. */}
          <p className="footnote text-muted">
            {me?.workspaceId
              ? 'Your workspace is ready.'
              : 'Next, open a workspace — or, if this is your first sign-in, set one up.'}
          </p>

          {/*
            A session that already has a workspace gets taken to what we found; one that does not
            gets taken to the thing that creates it.

            Both buttons used to be one button to /connect, so somebody whose tenant was already
            connected was offered only the screen asking them to connect it -- and nothing in the
            product linked to the dashboard at all. The way in was to know the URL.
          */}
          {/* No "Connect another Microsoft tenant" link any more: a workspace holds ONE tenant, and
              the link walked an owner through tenant-wide consent in a second directory only to be
              told a colleague owned it -- and left a read-only member on a screen with no way out.
              Re-approving permissions for the connected tenant lives in Settings. */}
          {me?.workspaceId ? (
            <Button onClick={() => navigate('/dashboard')}>Go to your dashboard</Button>
          ) : (
            <Button onClick={() => navigate('/connect')}>Continue</Button>
          )}
        </>
      )}
    </main>
  )
}
